import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { getBusinessHealth } from '../../../utils/dashboardService';

const BusinessHealthCards = () => {
  const [healthData, setHealthData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchHealthData = async () => {
      try {
        setLoading(true);
        const data = await getBusinessHealth();
        setHealthData(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching business health:', err);
        setError('Failed to load business health metrics');
      } finally {
        setLoading(false);
      }
    };

    fetchHealthData();
  }, []);

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  const getEngagementColor = (score) => {
    if (score >= 80) return 'accent';
    if (score >= 60) return 'primary';
    if (score >= 40) return 'secondary';
    return 'destructive';
  };

  const getEngagementStatus = (score) => {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Good';
    if (score >= 40) return 'Fair';
    return 'Needs Attention';
  };

  const getSupportHealthColor = (openTickets, avgResolutionHours) => {
    if (openTickets > 50 || avgResolutionHours > 48) return 'destructive';
    if (openTickets > 20 || avgResolutionHours > 24) return 'secondary';
    return 'accent';
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-8">
        {[...Array(4)].map((_, index) => (
          <div key={index} className="bg-card border border-border rounded-lg p-6 animate-pulse">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-muted rounded-lg"></div>
              <div className="w-16 h-4 bg-muted rounded"></div>
            </div>
            <div className="w-20 h-8 bg-muted rounded mb-2"></div>
            <div className="w-24 h-4 bg-muted rounded"></div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-6 mb-8">
        <div className="flex items-center space-x-2">
          <Icon name="AlertTriangle" size={20} className="text-destructive" />
          <span className="text-destructive font-medium">{error}</span>
        </div>
      </div>
    );
  }

  const cards = [
    {
      title: 'Platform Engagement',
      value: `${healthData?.engagementScore || 0}%`,
      subtitle: getEngagementStatus(healthData?.engagementScore || 0),
      icon: 'Activity',
      color: getEngagementColor(healthData?.engagementScore || 0),
      description: `${formatNumber(healthData?.monthlyActiveUsers || 0)} monthly active users`,
      trend: healthData?.engagementScore >= 60 ? 'positive' : 'negative'
    },
    {
      title: 'Game Library',
      value: formatNumber(healthData?.totalGames || 0),
      subtitle: `${healthData?.premiumRatio || 0}% premium • +${healthData?.newGamesThisMonth || 0} this month`,
      icon: 'Gamepad2',
      color: 'primary',
      description: `${healthData?.premiumGames || 0} premium, ${healthData?.freeGames || 0} free games`,
      trend: (healthData?.newGamesThisMonth || 0) > 0 ? 'positive' : 'neutral'
    },
    {
      title: 'Studio Partners',
      value: formatNumber(healthData?.activeStudios || 0),
      subtitle: 'Active studios',
      icon: 'Building2',
      color: 'secondary',
      description: 'Publishing partners',
      trend: 'neutral'
    },
    {
      title: 'Support Health',
      value: formatNumber(healthData?.supportMetrics?.openTickets || 0),
      subtitle: `${healthData?.supportMetrics?.avgResolutionHours || 0}h avg resolution`,
      icon: 'HeadphonesIcon',
      color: getSupportHealthColor(
        healthData?.supportMetrics?.openTickets || 0,
        healthData?.supportMetrics?.avgResolutionHours || 0
      ),
      description: `${healthData?.supportMetrics?.resolvedToday || 0} resolved today`,
      trend: (healthData?.supportMetrics?.openTickets || 0) < 20 ? 'positive' : 'negative'
    }
  ];

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Business Health</h2>
          <p className="text-sm text-muted-foreground">Key operational indicators</p>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
        {cards.map((card, index) => (
          <div key={index} className="bg-card border border-border rounded-lg p-6 hover:shadow-gaming transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                card.color === 'primary' ? 'bg-primary/10' :
                card.color === 'secondary' ? 'bg-secondary/10' :
                card.color === 'accent' ? 'bg-accent/10' :
                card.color === 'destructive' ? 'bg-destructive/10' : 'bg-muted/10'
              }`}>
                <Icon 
                  name={card.icon} 
                  size={24} 
                  className={
                    card.color === 'primary' ? 'text-primary' :
                    card.color === 'secondary' ? 'text-secondary' :
                    card.color === 'accent' ? 'text-accent' :
                    card.color === 'destructive' ? 'text-destructive' : 'text-muted-foreground'
                  }
                />
              </div>
              {card.trend !== 'neutral' && (
                <div className={`flex items-center space-x-1 ${
                  card.trend === 'positive' ? 'text-accent' : 'text-destructive'
                }`}>
                  <Icon 
                    name={card.trend === 'positive' ? 'TrendingUp' : 'TrendingDown'} 
                    size={16} 
                  />
                </div>
              )}
            </div>
            
            <div>
              <h3 className="text-2xl font-bold text-foreground mb-1">{card.value}</h3>
              <p className="text-sm text-muted-foreground mb-1">{card.title}</p>
              <p className="text-xs font-medium text-foreground mb-2">{card.subtitle}</p>
              <p className="text-xs text-muted-foreground/80">{card.description}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BusinessHealthCards;
